/** Site navigation shell shared by every Gatsby page. */
import 'rsuite/dist/rsuite.min.css';
import '@/styles/global.less';
import '@/styles/github-markdown.less';
// import '@/styles/github-gist.less';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';
import 'prismjs/plugins/command-line/prism-command-line.css';
import '@/styles/prismjs.less';
import 'typeface-jetbrains-mono';
import 'katex/dist/katex.min.css';

import { useLocation } from '@gatsbyjs/reach-router';
import { Link, withPrefix } from 'gatsby';
import { startsWith } from 'lodash';
import React, { useState } from 'react';
import {
  IconButton, Tooltip, Header, Whisper,
} from 'rsuite';

import { useSiteMetadata, useTheme } from '@/utils/hooks';
import Icon from '../../Icon';
import LoadableSearch from '../../SearchBar/loadable';
import SocialIconLink from '../../SocialIconLink';

import * as style from './header.module.less';

/** Render the optional light/dark mode toggle control. */
const ThemeModeSwitch = () => {
  const [themeMode, setThemeMode] = useTheme();
  const nextThemeMode = themeMode === 'light' ? 'dark' : 'light';

  const toggleTheme = () => {
    setThemeMode(nextThemeMode);
  };

  const tooltip = (
    <Tooltip>
      {`Switch to ${nextThemeMode} mode`}
    </Tooltip>
  );

  return (
    <Whisper placement="bottom" controlId="control-id-hover" trigger="hover" speaker={tooltip}>
      <IconButton
        size="sm"
        appearance="subtle"
        icon={<Icon size="lg" icon={themeMode === 'light' ? 'sun' : 'moon'} fixedWidth />}
        onClick={toggleTheme}
      />
    </Whisper>
  );
};

/** Render one navigation item with current-route styling. */
const NavButton = (props) => {
  const {
    onClick,
    to,
    children,
    partiallyActive,
  } = props;

  const location = useLocation();
  const isExternal = /^https?:\/\//.test(to);
  const prefixedTo = withPrefix(to);
  const encodedHref = encodeURI(prefixedTo);
  const isCurrent = location.pathname === encodedHref;
  const isPartiallyCurrent = startsWith(location.pathname, encodedHref);
  const showPrimary = !isExternal && (partiallyActive ? isPartiallyCurrent : isCurrent);
  const appearance = showPrimary ? 'primary' : 'subtle';
  const className = `rs-btn rs-btn-lg rs-btn-${appearance}`;

  if (isExternal) {
    return (
      <li className={style.navItem}>
        <a
          className={className}
          href={to}
          onClick={onClick}
        >
          {children}
        </a>
      </li>
    );
  }

  return (
    <li className={style.navItem}>
      <Link
        className={className}
        to={to}
        onClick={onClick}
      >
        {children}
      </Link>
    </li>
  );
};

/** Render the sticky top navigation bar and its drop-down menu on small screens. */
export default () => {
  const siteMetadata = useSiteMetadata();
  const headerSocial = (siteMetadata.social || []).filter((social) => social.header);
  const moreSocial = (siteMetadata.social || []).filter((social) => social.headerMore);
  const [menu, setMenu] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isSearchBarExpanded, setSearchBarExpanded] = useState(false);
  // const searchBarRef = useRef(null);

  const toggleMenu = () => setMenu(!menu);
  const closeMenu = () => setMenu(false);

  const toggleSearchIconClick = (flag) => {
    setSearchBarExpanded(flag);
  };

  const expandSearch = () => {
    toggleSearchIconClick(true);
  };

  const collapseSearch = () => {
    toggleSearchIconClick(false);
  };

  // One bar at every width: brand left, links centered, search and icons right.
  // Below 1024px the links move into a drop-down opened by the menu button.
  return (
    <Header className={style.headerShell}>
      <div className={style.bar}>
        <Link className={style.brandLink} to="/" onClick={closeMenu}>
          <img className={style.brandMark} src={withPrefix('/husky-icon.png')} alt="" />
          <span className={style.brandText}>HuskyDoge</span>
        </Link>
        <nav className={`${style.navWrap} ${menu ? style.openMenu : ''}`}>
          <ul className={style.nav}>
            <NavButton to="/" onClick={closeMenu}>About</NavButton>
            <NavButton to="/research/" onClick={closeMenu} partiallyActive>Research</NavButton>
            {/* Hidden from the menu for now; the pages still build at their URLs.
            <NavButton to="/project/" onClick={closeMenu} partiallyActive>Project</NavButton>
            <NavButton to="/bookshelf/" onClick={closeMenu} partiallyActive>Bookshelf</NavButton>
            */}
            <NavButton to="https://huskydoge.github.io/husky-blog" onClick={closeMenu} partiallyActive>Blogs</NavButton>
            <NavButton to="/misc/" onClick={closeMenu} partiallyActive>Misc</NavButton>
            {/* <li className={style.navItem}>
              <ThemeModeSwitch />
            </li> */}
          </ul>
        </nav>
        <div className={style.barRight}>
          <span className={style.searchItem}>
            <LoadableSearch
              isSearchBarExpanded={isSearchBarExpanded}
              handleSearchBarToggle={collapseSearch}
            />
            {isSearchBarExpanded
              ? <Icon icon="times" fixedWidth />
              : <Icon icon="search" fixedWidth onMouseDown={expandSearch} />}
          </span>
          {headerSocial.map((social) => (
            <SocialIconLink
              key={social.label || social.url}
              social={social}
              className={style.headerSocialAction}
            />
          ))}
          {moreSocial.length > 0 ? (
            // The arrow opens the remaining links on hover, or on click for touch and keyboard.
            <span
              className={`${style.moreSocial} ${moreOpen ? style.moreSocialOpen : ''}`}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                type="button"
                className={`${style.headerSocialAction} ${style.moreToggle}`}
                aria-label={moreOpen ? 'Hide more links' : 'Show more links'}
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen(!moreOpen)}
              >
                <Icon icon="angle-right" fixedWidth />
              </button>
              <span className={style.moreSocialItems}>
                {moreSocial.map((social) => (
                  <SocialIconLink
                    key={social.label || social.url}
                    social={social}
                    className={style.headerSocialAction}
                  />
                ))}
              </span>
            </span>
          ) : null}
          <button
            type="button"
            className={`${style.menuButton} ${menu ? style.menuButtonOpen : ''}`}
            aria-label={menu ? 'Close menu' : 'Open menu'}
            aria-expanded={menu}
            onClick={toggleMenu}
          >
            <span className={style.line} />
            <span className={style.line} />
          </button>
        </div>
      </div>
    </Header>
  );
};
